import React from 'react';
import {View, StyleSheet, ViewStyle, TextStyle, Platform} from 'react-native';
import CustomTextInput from 'src/components/elements/CustomTextInput';
import AVText from 'src/components/elements/AVText';
import Localized from 'src/constants/AppStrings';
import Styles from 'src/components/Styles';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import Settings from 'src/Settings';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
const EXTRA_BOTTOM_WHEN_ERROR =
  Styles.Heights.touchTargetHeight1 + Styles.Spacing.m6;

export type BirthMonthYearInputProps = {
  birthYear: string;
  birthMonthYear: string;
  birthMonthYearError?: string;
  onChangeText: (text: string) => void;
  containerStyle?: ViewStyle;
  textInputStyle?: ViewStyle | TextStyle;
  label?: string;
  placeholder?: string;
  buttonAction: () => void;
  _isBtnActive?: boolean;
};

const BirthMonthYearInput: React.FC<BirthMonthYearInputProps> = ({
  birthYear,
  birthMonthYear,
  birthMonthYearError = '',
  onChangeText,
  containerStyle,
  textInputStyle,
  label = Localized.Labels.redesign_birthday,
  placeholder = 'MM/YYYY',
  buttonAction,
  _isBtnActive,
}) => {
  const isRefive365PayMode = Settings.isRefiveAnd365PayOnly();
  return (
    <View
      accessible
      accessibilityLabel="Birth Year"
      accessibilityRole="menu"
      accessibilityValue={{text: birthYear}}
      role="menu"
      aria-label="Birth Year"
      aria-valuetext={birthYear}
      style={[styles.container, containerStyle]}
    >
      <View accessible>
        <AVText
          style={styles.headerLabel}
          accessibilityLabel={Localized.Labels.add_birthday_to_earn_points}
          aria-label={Localized.Labels.add_birthday_to_earn_points}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
        >
          {Localized.Labels.add_birthday_to_earn_points}
        </AVText>
      </View>
      <CustomTextInput
        label={label}
        value={birthMonthYear}
        accessible
        onChangeText={onChangeText}
        accessibilityLabel={label}
        accessibilityValue={{text: birthMonthYear}}
        style={[styles.txtInput, textInputStyle]}
        autoCapitalize="none"
        keyboardType="number-pad"
        maxLength={7}
        returnKeyType="done"
        maxFontSizeMultiplier={Styles.FontSizeMultiplier?.maxfm5}
        showShadow={Platform.OS === 'ios'}
        placeholder={placeholder}
        outlineStyle={[
          styles.outline,
          birthMonthYearError && styles.errorBorder,
        ]}
        theme={{
          colors: {
            error: Styles.Colors.PayNew.error,
          },
        }}
        error={!!birthMonthYearError}
      />
      {!!birthMonthYearError && (
        <AVText accessibilityRole="text" style={styles.errorText}>
          {birthMonthYearError}
        </AVText>
      )}

      <RoundedAppButton
        accessibilityLabelValue={Localized.Buttons.save}
        accessibilityHintValue="Double tap to save your card information"
        aria-label={Localized.Buttons.save}
        buttonType={RoundedAppButtonType.Solid}
        onPress={buttonAction}
        titleText={Localized.Buttons.save}
        isDisabled={!_isBtnActive}
        buttonContainerStyle={[
          isRefive365PayMode
            ? styles.btnContainerStyleT1
            : styles.btnContainerStyleT2,
          birthMonthYearError && styles.errorBtnContainerStyle,
        ]}
        textStyle={[styles.btnTextStyle]}
        inActiveTextStyle={[styles.btnTextStyle]}
        buttonViewStyle={[
          Settings.isRefiveAnd365PayOnly()
            ? styles.btnViewStyleT1
            : styles.btnViewStyleT2,
          {
            backgroundColor: _isBtnActive
              ? scanAndPayTemplate.primaryColor
              : Styles.Colors.PayNew.darkHuesBase04,
          },
        ]}
        inActiveButtonViewStyle={
          Settings.isRefiveAnd365PayOnly()
            ? styles.btnViewStyleT1
            : styles.btnViewStyleT2
        }
        testID="qa_btn_Save"
      />
      <View style={styles.bottomHeight} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    paddingVertical: Styles.Spacing.m3,
  },
  txtInput: {
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  errorText: {
    color: Styles.Colors.PayNew.error,
    marginTop: Styles.Spacing.m1,
    marginLeft: Styles.Spacing.m1,
  },
  headerLabel: {
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    color: Styles.Colors.PayNew.black01,
    fontWeight: '700',
    fontFamily: scanAndPayTemplate.textFontFamily,
    alignItems: 'center',
  },
  btnContainerStyleT1: {
    alignSelf: 'flex-end',
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m3,
  },
  btnContainerStyleT2: {
    width: Platform.OS === 'web' ? '100%' : Styles.ScreenWidth,
    alignSelf: 'center',
    justifyContent: 'center',
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  btnViewStyleT2: {
    width: '100%',
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
    height: Styles.Heights.touchTargetHeight1 - 2,
  },
  btnViewStyleT1: {
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
    height: Styles.Heights.touchTargetHeight1 - 2,
    paddingHorizontal: Styles.Spacing.m3,
  },
  btnTextStyle: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    color: Styles.white,
    paddingHorizontal: Styles.Spacing.m3,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  outline: {
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
  },
  errorBorder: {borderColor: Styles.Colors.PayNew.error},
  errorBtnContainerStyle: {
    marginTop:
      Platform.OS === 'ios'
        ? Styles.Spacing.m1
        : Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  bottomHeight: {height: EXTRA_BOTTOM_WHEN_ERROR},
});

export default React.memo(BirthMonthYearInput);
